import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {useLocation, useParams, useHistory} from 'react-router-dom'

import {usePage, useProductSearch} from '@salesforce/commerce-sdk-react'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

// Components
import {useDisclosure} from '@salesforce/retail-react-app/app/components/shared/ui'

// Hooks
import useMultiSite from '../../../hooks/use-multi-site'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import useEinstein from '../../../hooks/use-einstein'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'

// Others
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getCategoryLevel, getEnvStaleTime} from '../../../utils/utils'

// Constants
import {
    MAX_CACHE_AGE,
    STALE_WHILE_REVALIDATE,
    DEFAULT_SEARCH_PARAMS
} from '@salesforce/retail-react-app/app/constants'
import {
    SEARCH_LIMIT_VALUE,
    SEARCH_PARAMS_LIMIT,
    PRODUCT_SEARCH_PRODUCT_TYPE_REFINEMENT
} from '../../../constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useShipToCountry from '../../../hooks/use-ship-to-country'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

// Redirect with Status
import RedirectWithStatus from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/redirect-with-status'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']
export const EXCLUDED_FILTERS_LIST = [
    'cgid',
    'c_subCategoryID',
    'c_orliweb_stat03_code_marche',
    'c_orliweb_stat20_mat_rgt',
    'c_color',
    'c_orliweb_stat10_type_prod'
]

/**
 * Creates a Context object for managing the state and behavior of the product list component.
 */
const ProductListContext = React.createContext()

/**
 * Provides a context for managing and interacting with a product list, including search, filtering,
 * sorting, and wishlist functionalities. This component acts as a provider for the `ProductListContext`,
 * supplying various states, actions, and utilities related to product listing.
 */
export const ProductListProvider = ({productListProps, children}) => {
    // Using destructuring to omit properties; we must rename `isLoading` because we use a different
    // `isLoading` later in this function.
    const {buildUrl} = useMultiSite()
    const {isLoading: _unusedIsLoading, staticContext, ...productListPropsRest} = productListProps
    const {
        isOpen: isOpenRefinements,
        onOpen: onOpenRefinements,
        onClose: onCloseRefinements
    } = useDisclosure()
    const navigate = useNavigation()
    const params = useParams()
    const location = useLocation()
    const history = useHistory()
    const einstein = useEinstein()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const refinementsOpenRef = useRef()
    const refinementsCloseRef = useRef()
    const {triggerViewItemListEvent} = useGTM()

    /**************** Page State ****************/
    const [filtersLoading, setFiltersLoading] = useState(false)

    const urlParams = new URLSearchParams(location.search)
    const pageParam = parseInt(urlParams.get('p')) || 0
    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery

    /**************** Edito Logic ****************/
    const staleTime = getEnvStaleTime()
    const {data: editoPage} = usePage(
        {parameters: {pageId: params?.categoryId}},
        {enabled: params?.categoryId ? true : false, staleTime: staleTime}
    )

    /**************** Mosaic Logic***************/
    // Determine if the current path includes '/mosaic/' and is mosaic type
    const isMosaicPath = location.pathname.includes('/mosaic/')
    const isMosaicType = productListProps?.category?.c_categoryType === 'mosaic'
    const isMosaic = isMosaicType && isMosaicPath // not fetch products for Mosaic PLP version
    const mosaicTemplate = isMosaicPath ? 'mosaic' : 'classic'
    const isFilter = !isMosaic // no filter on Mosaic PLP
    const productSearchParams = isMosaic
        ? {select: `(total)`} // total fetch only
        : {
              allVariationProperties: true,
              allImages: true,
              expand: [
                  'promotions',
                  'variations',
                  'prices',
                  'images',
                  'custom_properties',
                  'page_meta_tags'
              ]
          }

    /************** Choose template ***************/
    const categoryType = isMosaicType
        ? mosaicTemplate // ignore mosaic type on none mosaic path
        : editoPage
        ? 'editorial'
        : productListProps?.category?.c_categoryType || ''

    useEffect(() => {
        if (productListProps?.category?.c_categoryType && isMosaicPath && !isMosaicType) {
            // Update the URL without reloading the page
            history.replace(buildUrl(`/category/${params.categoryId}`))
        }
    }, [productListProps?.category?.c_categoryType, isMosaicPath, isMosaicType])

    /*********************** Category ************************/
    const categoryLevel = getCategoryLevel(
        productListProps?.category?.id,
        productListProps?.category?.parentCategoryTree
    )
    // On server side rendering, we cannot use data from an API call to use it in another API call in PWA kit,
    // so, as category is not fetched yet, we always display maximum 100 products for web crawlers
    const categoryLimit = !isServer ? SEARCH_PARAMS_LIMIT : 100
    const initialOffset = !isSearch
        ? pageParam > 1
            ? (pageParam - 1) * SEARCH_PARAMS_LIMIT
            : pageParam === 1
            ? SEARCH_PARAMS_LIMIT
            : 0
        : 0
    let searchParamsParams = isSearch
        ? {...DEFAULT_SEARCH_PARAMS, limit: SEARCH_LIMIT_VALUE}
        : {...DEFAULT_SEARCH_PARAMS, limit: categoryLimit, offset: initialOffset}
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams(searchParamsParams)

    if (params.categoryId) {
        searchParams._refine.push(`cgid=${params.categoryId}`)
    }

    // Only retrieve variation groups and sets (exclude other product types)
    searchParams._refine.push(PRODUCT_SEARCH_PRODUCT_TYPE_REFINEMENT)

    /**************** Query Actions ****************/
    // _refine is an invalid param for useProductSearch, we don't want to pass it to API call
    const {_refine, ...restOfParams} = searchParams
    const {shipToCountry} = useShipToCountry()

    const {
        isLoading,
        isFetched,
        isRefetching,
        data: productSearchResult
    } = useProductSearch(
        {
            parameters: {
                ...restOfParams,
                ...productSearchParams,
                refine: _refine,
                c_country: shipToCountry?.id // required when country changes
            }
        },
        {
            keepPreviousData: false,
            enabled: !isServer && restOfParams?.limit && restOfParams?.limit > 0
        }
    )
    const isProductListLoading = isLoading || isRefetching

    // Apply disallow list to refinements.
    if (productSearchResult?.refinements) {
        productSearchResult.refinements = productSearchResult.refinements.filter(
            ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
        )
    }

    /**************** Response Handling ****************/
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    // Reset scroll position when `isRefetching` becomes `true`.
    useEffect(() => {
        isRefetching && window.scrollTo(0, 0)
        setFiltersLoading(isRefetching)
    }, [isRefetching])

    useEffect(() => {
        // GTM Integration
        if (productSearchResult && productSearchResult?.hits) {
            triggerViewItemListEvent(productSearchResult?.hits)
        }
    }, [productSearchResult])

    /**************** Render Variables ****************/
    const showNoResults =
        !isMosaic && !isLoading && productSearchResult && !productSearchResult?.hits
    const {total, sortingOptions} = productSearchResult || {}
    const selectedSortingOptionLabel =
        sortingOptions?.find(
            (option) => option.id === productSearchResult?.selectedSortingOption
        ) ?? sortingOptions?.[0]

    /**************** Action Handlers on Filter ****************/
    let toggleFilter, resetFilters

    // page metatags
    const metatags = productSearchResult?.pageMetaTags

    if (isFilter) {
        // Toggles filter on and off
        toggleFilter = (value, attributeId, selected, allowMultiple = true) => {
            const searchParamsCopy = {...searchParams}

            // Remove the `offset` search param if present.
            delete searchParamsCopy.offset

            // If we aren't allowing for multiple selections, simply clear any value set for the
            // attribute, and apply a new one if required.
            if (!allowMultiple) {
                const previousValue = searchParamsCopy.refine[attributeId]
                delete searchParamsCopy.refine[attributeId]

                // Note the loose comparison, for "string != number" checks.
                if (!selected && value.value != previousValue) {
                    searchParamsCopy.refine[attributeId] = value.value
                }
            } else {
                // Get the attibute value as an array.
                let attributeValue = searchParamsCopy.refine[attributeId] || []

                // Ensure that the value is still converted into an array if it's a `string` or `number`.
                if (typeof attributeValue === 'string') {
                    attributeValue = attributeValue.split('|')
                } else if (typeof attributeValue === 'number') {
                    attributeValue = [attributeValue]
                }

                // Either set the value, or filter the value out.
                if (!selected) {
                    attributeValue.push(value.value)
                } else {
                    // Note the loose comparison, for "string != number" checks.
                    attributeValue = attributeValue?.filter((v) => v != value.value)
                }

                // Update the attribute value in the new search params.
                searchParamsCopy.refine[attributeId] = attributeValue

                // If the update value is an empty array, remove the current attribute key.
                if (searchParamsCopy.refine[attributeId].length === 0) {
                    delete searchParamsCopy.refine[attributeId]
                }
            }

            if (isSearch) {
                navigate(`/search?${stringifySearchParams(searchParamsCopy)}`)
            } else {
                navigate(
                    `/category/${params.categoryId}?${stringifySearchParams(searchParamsCopy)}`
                )
            }
        }

        // Clears all filters or specific filter
        resetFilters = (filterId = '') => {
            // Clone the current search parameters
            const newSearchParams = {...searchParams}

            if (filterId.length) {
                // Remove the specific attribute from the refine object
                if (newSearchParams?.refine?.[filterId]) {
                    delete newSearchParams.refine[filterId]
                }
            } else {
                // Clear all filters
                newSearchParams.refine = []
            }

            const newPath = isSearch
                ? `/search?${stringifySearchParams(newSearchParams)}`
                : `/category/${params.categoryId}?${stringifySearchParams(newSearchParams)}`

            navigate(newPath)
        }
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (productSearchResult) {
            if (isSearch) {
                try {
                    einstein.sendViewSearch(searchQuery, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewSearch error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, searchQuery}
                    })
                }
                activeData.sendViewSearch(searchParams, productSearchResult)
            } else if (productListProps?.category) {
                try {
                    einstein.sendViewCategory(productListProps?.category, productSearchResult)
                } catch (err) {
                    logger.error('Einstein sendViewCategory error', {
                        namespace: 'ProductList.useEffect',
                        additionalProperties: {error: err, category: productListProps?.category}
                    })
                }
                activeData.sendViewCategory(
                    searchParams,
                    productListProps?.category,
                    productSearchResult
                )
            }
        }
    }, [productSearchResult])

    const values = {
        // product list props
        _unusedIsLoading,
        staticContext,
        productListPropsRest,
        // Filter drawer
        isOpenRefinements,
        onOpenRefinements,
        onCloseRefinements,
        refinementsOpenRef,
        refinementsCloseRef,
        searchParams,
        // Page state
        filtersLoading,
        // urlParams,
        searchQuery,
        // Product Search
        isLoading,
        isFetched,
        isRefetching,
        isProductListLoading,
        productSearchResult,
        metatags,
        // Category
        categoryType,
        isSearch,
        category: productListProps?.category,
        categoryLevel,
        // Render Variables
        showNoResults,
        total,
        sortingOptions,
        selectedSortingOptionLabel,
        // Toggles filter on and off
        toggleFilter,
        // Clears all filters
        resetFilters,
        // Edito
        editoPage
    }

    /* Redirect to the last page if the pageParam is greater than totalPages */
    const totalPages = Math.ceil(total / SEARCH_PARAMS_LIMIT)
    if (isServer && total !== undefined && total > 0) {
        if (pageParam > totalPages) {
            return <RedirectWithStatus status={302} to={`${location.pathname}?p=${totalPages}`} />
        }
    }

    return <ProductListContext.Provider value={values}>{children}</ProductListContext.Provider>
}

ProductListProvider.propTypes = {
    productListProps: PropTypes.object,
    children: PropTypes.node
}

// Custom hook to use the ProductListContext
export const useProductList = () => {
    return React.useContext(ProductListContext)
}
